"use client";

import { useLayoutEffect, useRef, useState, type RefObject } from "react";

import type { Locale } from "@/lib/i18n/locales";
import rolloutCopy from "@/messages/mandarin-rollout.json";

import styles from "./tour-orientation.module.css";

type Props = {
  locale?: Locale;
  orbitTarget: RefObject<HTMLButtonElement | null>;
  viewsTarget: RefObject<HTMLElement | null>;
  walkTarget: RefObject<HTMLButtonElement | null>;
  onOpen: () => void;
  onClose: () => void;
};

const steps: { title: string; body: string; touchBody?: string }[] = [
  {
    title: "Look around",
    body: "Drag to rotate the model. Scroll to zoom.",
    touchBody: "Drag to rotate the model. Pinch to zoom.",
  },
  {
    title: "Jump to a room",
    body: "Choose a view below to visit a room. Overview shows the whole lab.",
  },
  {
    title: "Walk inside",
    body: "In Walk, use W A S D to move and the mouse to look. Press Esc to leave.",
    touchBody:
      "In Walk, steer with the left joystick and drag the right side to look. Choose Orbit to leave.",
  },
];

export default function TourOrientation({
  locale = "en",
  orbitTarget,
  viewsTarget,
  walkTarget,
  onOpen,
  onClose,
}: Props) {
  const copy = rolloutCopy.virtualTour.orientation;
  const localSteps: typeof steps = locale === "zh-Hans" ? copy.steps : steps;
  const [step, setStep] = useState<number | null>(null);
  const [touchInput, setTouchInput] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const open = step !== null;

  useLayoutEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current!;
    const trigger = triggerRef.current;
    dialog.showModal();
    headingRef.current?.focus();
    return () => {
      dialog.close();
      trigger?.focus();
    };
  }, [open]);

  useLayoutEffect(() => {
    if (step === null) return;
    const target = [orbitTarget, viewsTarget, walkTarget][step].current;
    const popup = popupRef.current;
    const highlight = highlightRef.current;
    const dialog = dialogRef.current;
    if (!target || !popup || !highlight || !dialog) return;

    function position() {
      if (!target || !popup || !highlight || !dialog) return;
      const rect = target.getBoundingClientRect();
      const width = dialog.clientWidth || window.innerWidth;
      const height = dialog.clientHeight || window.innerHeight;
      const gap = 12;
      const popupWidth = popup.offsetWidth;
      const popupHeight = popup.offsetHeight;
      const left = Math.max(
        gap,
        Math.min(rect.right - popupWidth, width - popupWidth - gap)
      );
      const preferredTop =
        step === 1 ? rect.top - popupHeight - gap : rect.bottom + gap;
      const top = Math.max(
        gap,
        Math.min(preferredTop, height - popupHeight - gap)
      );
      Object.assign(popup.style, { left: `${left}px`, top: `${top}px` });
      Object.assign(highlight.style, {
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      });
    }

    position();
    // Reflow can come from viewport changes, font loading or text enlargement.
    const observer = new ResizeObserver(position);
    observer.observe(target);
    observer.observe(popup);
    observer.observe(dialog);
    window.addEventListener("resize", position);
    window.visualViewport?.addEventListener("resize", position);
    headingRef.current?.focus();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", position);
      window.visualViewport?.removeEventListener("resize", position);
    };
  }, [step, orbitTarget, viewsTarget, walkTarget]);

  function close() {
    setStep(null);
    onClose();
  }

  return (
    <>
      <button
        ref={triggerRef}
        className={styles.trigger}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setTouchInput(
            window.matchMedia("(pointer: coarse)").matches ||
              (navigator.maxTouchPoints > 0 &&
                window.matchMedia("(hover: none)").matches)
          );
          onOpen();
          setStep(0);
        }}
      >
        {locale === "zh-Hans" ? copy.trigger : "First time?"}
      </button>
      {step !== null ? (
        // Native modal dialog owns Escape and keyboard focus containment.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <dialog
          ref={dialogRef}
          className={styles.layer}
          aria-modal="true"
          aria-labelledby="tour-orientation-title"
          aria-describedby="tour-orientation-description"
          onCancel={(event) => {
            event.preventDefault();
            close();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Escape") {
              event.preventDefault();
              close();
              return;
            }
            if (event.key !== "Tab") return;
            const buttons = [
              ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
                "button:not(:disabled)"
              ),
            ];
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            if (
              event.shiftKey &&
              (document.activeElement === first ||
                document.activeElement === headingRef.current)
            ) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }}
        >
          <div
            ref={highlightRef}
            className={styles.highlight}
            aria-hidden="true"
            data-tour-highlight={step}
          />
          <div
            ref={popupRef}
            className={styles.popup}
            data-tour-step={step + 1}
          >
            <h2 id="tour-orientation-title" ref={headingRef} tabIndex={-1}>
              {step + 1}. {localSteps[step].title}
            </h2>
            <p id="tour-orientation-description">
              {touchInput
                ? localSteps[step].touchBody ?? localSteps[step].body
                : localSteps[step].body}
            </p>
            <div className={styles.actions}>
              <button type="button" onClick={close}>
                {locale === "zh-Hans" ? copy.close : "Close"}
              </button>
              <div>
                <button
                  type="button"
                  disabled={step === 0}
                  onClick={() => setStep(step - 1)}
                >
                  {locale === "zh-Hans" ? copy.back : "Back"}
                </button>
                <button
                  type="button"
                  className={styles.next}
                  onClick={() => (step === 2 ? close() : setStep(step + 1))}
                >
                  {step === 2
                    ? locale === "zh-Hans"
                      ? copy.done
                      : "Done"
                    : locale === "zh-Hans"
                    ? copy.next
                    : "Next"}
                </button>
              </div>
            </div>
          </div>
        </dialog>
      ) : null}
    </>
  );
}
